import { api } from './api'

function assertSuccess(body: unknown) {
  const b = body as { success?: boolean; message?: string; data?: unknown }
  if (!b?.success) {
    throw new Error(b?.message || 'Request failed')
  }
  return b.data
}

function apiError(err: unknown, fallback: string) {
  const e = err as { response?: { data?: { message?: string } }; message?: string }
  return new Error(e?.response?.data?.message || e?.message || fallback)
}

/** Sequelize/JSON columns may return arrays or stringified JSON. */
function normalizeCurrencies(raw: unknown): string[] {
  if (raw == null) return []
  if (Array.isArray(raw)) {
    return raw.map((c) => String(c).trim().toUpperCase()).filter((c) => /^[A-Z]{3}$/.test(c))
  }
  if (typeof raw === 'string') {
    const t = raw.trim()
    if (!t) return []
    try {
      return normalizeCurrencies(JSON.parse(t) as unknown)
    } catch {
      return []
    }
  }
  return []
}

function normalizeGatewayRow(row: PlatformPaymentGatewayRow): PlatformPaymentGatewayRow {
  return {
    ...row,
    supportedCurrencies: normalizeCurrencies(row.supportedCurrencies),
  }
}

export type PlatformPaymentGatewayRow = {
  id: number
  slug: string
  provider: string
  displayName: string
  logoUrl: string | null
  supportedCurrencies: string[]
  isGloballyActive: boolean
  sortOrder: number
  enabledStoreCount?: number
  createdAt?: string
  updatedAt?: string
}

export type ListGatewaysResult = { items: PlatformPaymentGatewayRow[] }

export async function listPlatformPaymentGateways(): Promise<ListGatewaysResult> {
  try {
    const { data } = await api.get('/admin/payment-gateways')
    const result = assertSuccess(data) as ListGatewaysResult
    return {
      items: (result.items || []).map(normalizeGatewayRow),
    }
  } catch (e) {
    throw apiError(e, 'Failed to load payment gateways')
  }
}

export async function createPlatformPaymentGateway(payload: {
  provider: string
  displayName: string
  logoUrl?: string | null
  isGloballyActive?: boolean
  sortOrder?: number
  supportedCurrencies?: string[]
  secretKey?: string
  publicKey?: string
}): Promise<PlatformPaymentGatewayRow> {
  try {
    const { data } = await api.post('/admin/payment-gateways', payload)
    return normalizeGatewayRow(assertSuccess(data) as PlatformPaymentGatewayRow)
  } catch (e) {
    throw apiError(e, 'Failed to create payment gateway')
  }
}

export async function patchPlatformPaymentGateway(
  id: number,
  payload: Partial<{
    displayName: string
    logoUrl: string | null
    isGloballyActive: boolean
    sortOrder: number
    supportedCurrencies: string[]
    secretKey: string
    publicKey: string
  }>,
): Promise<PlatformPaymentGatewayRow> {
  try {
    const { data } = await api.patch(`/admin/payment-gateways/${encodeURIComponent(id)}`, payload)
    return normalizeGatewayRow(assertSuccess(data) as PlatformPaymentGatewayRow)
  } catch (e) {
    throw apiError(e, 'Failed to update payment gateway')
  }
}
